import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Offer {
  offerType: string;
  offerId: string;
  offerDetails: {
    description: string;
    endDate: string;
    startDate: string;
  };
}

export interface ViewOffersResponse {
  availableOffers?: string,
  offerType?: string,
  cliResponse?: {
    current_credit_limit: string;
    offer_status: string;
    offered_credit_limit: string;
    expiry_date: string;
    errorMessage?: string;
  };
  scoResponse?: {
    offer_status: string;
    expiry_date: string;
    errorMessage?: string;
  };
  btoffersResponse?: {
    availableOffers?: Array<{
      offerType: string;
      offerId: string;
      offerDetails: {
        description: string;
        endDate: string;
        startDate: string;
      };
    }>;
    message?: {
      status: string;
      detail: string;
      title: string;
      errorCode?: string;
      errors?: string[];
    };
  };
  resiResponse?: {
    url: string;
    expiry_date: string;
    errorMessage: string;
  };
  message?: {
    status: string;
    detail: string;
    title: string;
    errorCode?: string;
    errors?: string[];
  };
  
}

export interface OfferState {
  loading: boolean;
  offers: Offer[];
  error: string | null;
  offerPreference: any | null;
  targetedOfferPrefSubmit: any | null;
  viewOffers: ViewOffersResponse | null;
  viewOffersLoading: boolean;
  viewOffersError: string | null;
}

const initialState: OfferState = {
  loading: false,
  offers: [],
  error: null,
  offerPreference: null,
  targetedOfferPrefSubmit: null,
  viewOffers: null,
  viewOffersLoading: false,
  viewOffersError: null,
};

const offerSlice = createSlice({
  name: 'offer',
  initialState,
  reducers: {
    fetchOfferRequest(state, action) {
      state.loading = true;
      state.error = null;
    },
    fetchOfferSuccess(state, action: PayloadAction<Offer | Offer[]>) {
      state.loading = false;
      state.offers = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];
      state.error = null;
    },
    fetchOfferFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    fetchOfferPreferenceRequest(state, action) {
      state.loading = true;
      state.error = null;
    },
    fetchOfferPreferenceSuccess(state, action) {
      state.loading = false;
      state.offerPreference = action.payload;
      state.error = null;
    },
    fetchOfferPreferenceFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    fetchOfferPrefSubmitRequest(state, action) {
      state.loading = true;
      state.error = null;
    },
    fetchOfferPrefSubmitSuccess(state, action) {
      state.loading = false;
      state.targetedOfferPrefSubmit = action.payload;
      state.error = null;
    },
    fetchOfferPrefSubmitFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    fetchViewOffersRequest(state, action) {
      state.viewOffersLoading = true;
      state.viewOffersError = null;
    },
    fetchViewOffersSuccess(state, action: PayloadAction<ViewOffersResponse>) {
      state.viewOffersLoading = false;
      state.viewOffers = action.payload;
      state.viewOffersError = null;
    },
    fetchViewOffersFailure(state, action) {
      state.viewOffersLoading = false;
      state.viewOffersError = action.payload;
    },
    resetOffersSlice(state){
      return initialState;
    }
  },
});

export const {
  fetchOfferRequest,
  fetchOfferSuccess,
  fetchOfferFailure,
  fetchOfferPreferenceFailure,
  fetchOfferPreferenceSuccess,
  fetchOfferPreferenceRequest,
  fetchOfferPrefSubmitRequest,
  fetchOfferPrefSubmitSuccess,
  fetchOfferPrefSubmitFailure,
  fetchViewOffersRequest,
  fetchViewOffersSuccess,
  fetchViewOffersFailure,
  resetOffersSlice,
} = offerSlice.actions;

export default offerSlice.reducer;
